import React, { useState, useEffect } from 'react';
import { PartyConsentEntry, ConsentDomainDefault, ConsentInvite, ConsentReceiptSummary, VerifiedReceipt } from '../types';
import { consentAdminService, portalAdminService, fetchReceipt, downloadReceiptBundle, ConsentFlags, ConsentProof, CONSENT_PROOF_TYPES, CONSENT_LAWFUL_BASES, LAWFUL_BASIS_LABELS } from '../services/api';
import { formatDateTime } from '../utils/datetime';
import ScopeFilter, { ScopeSelection } from './ScopeFilter';

// Consent management: the party consent registry and the domain-default
// policy that feeds it, as two sub-pages (domains can grow to hundreds).
// Styled after the 12by9 system-diagram design language (indigo/gold,
// eyebrow header, dark policy panel, white cards).
//
// Semantics: these are defaults applied when a NEW vCon is created — a
// registered party contributes its purposes; an unknown party is
// auto-registered from its domain's defaults ('*' = fallback). Existing
// vCons keep their own consent (VCons page -> Consent).

const PURPOSES = [
  { key: 'consent_recording' as const, label: 'recording' },
  { key: 'consent_transcription' as const, label: 'transcription' },
  { key: 'consent_analysis' as const, label: 'analysis' },
  { key: 'consent_ai_training' as const, label: 'ai_training' },
];

type FlagKey = typeof PURPOSES[number]['key'];

const flagsOf = (row: ConsentFlags): ConsentFlags => ({
  consent_recording: !!row.consent_recording,
  consent_transcription: !!row.consent_transcription,
  consent_analysis: !!row.consent_analysis,
  consent_ai_training: !!row.consent_ai_training,
});

const apiErrorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.error || fallback;

const PROOF_LABELS: Record<string, string> = {
  attested: 'attested',
  a_priori_agreement: 'agreement',
  announcement: 'announcement',
  dtmf: 'DTMF',
  verbal: 'verbal',
  portal_receipt: 'receipt',
};

// Views one signed receipt: fetches it from the public verification
// endpoint, shows signature validity, and offers the download bundle.
//
// Presented the same way as the receipt page the endpoint serves to a browser
// (portal_receipt_verify.php) and the same way the Horizon consent page shows
// one: the verification result first, then what was agreed in labelled fields,
// then the signed bytes for anyone who wants to check it themselves. A receipt
// is evidence a person may have to read and act on, so it should not look
// different depending on which console they happened to open it from.
const NETWORK_NAMES: Record<string, string> = {
  whatsapp: 'WhatsApp', messenger: 'Messenger', instagram: 'Instagram', googlechat: 'Google Chat', matrix: 'Matrix',
};

const ReceiptModal: React.FC<{ receiptId: string; onClose: () => void }> = ({ receiptId: firstId, onClose }) => {
  // A consent receipt and its proof receipt link to each other; following a
  // link shows the other one here.
  const [receiptId, setReceiptId] = useState(firstId);
  const [verified, setVerified] = useState<VerifiedReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showBytes, setShowBytes] = useState(false);

  useEffect(() => {
    setVerified(null);
    setError(null);
    fetchReceipt(receiptId)
      .then(setVerified)
      .catch(err => setError(err?.response?.data?.error || 'Failed to load the receipt.'));
  }, [receiptId]);

  const r: any = verified?.receipt ?? {};
  const subject = r.data_subject ?? {};
  const applies = r.applies_to ?? {};
  const agreement = r.agreement ?? {};
  const isProof = r.type === 'identity_proof';
  const proves = r.proves ?? {};
  const identity = r.identity ?? {};
  const evidence = r.evidence ?? {};
  const receiptLink = (id: string) => (
    <button type="button" className="rcpt-toggle monospace small" onClick={() => setReceiptId(id)}>{id}</button>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isProof ? 'Proof receipt' : 'Consent receipt'}</h3>
          <button type="button" onClick={onClose}>Close</button>
        </div>
        <div className="modal-subtitle monospace small">{receiptId}</div>
        {error && <div className="error" style={{ marginTop: '0.75rem' }}>{error}</div>}
        {!verified && !error && <div className="small" style={{ marginTop: '0.75rem' }}>Loading and verifying…</div>}
        {verified && (
          <>
            <div className={`rcpt-badge ${verified.signature_valid ? 'ok' : 'bad'}`}>
              {verified.signature_valid
                ? '✓ Signature valid — this receipt is authentic and unaltered'
                : '✗ Signature INVALID — do not rely on this receipt'}
            </div>

            {isProof ? (
            <div className="cp-card">
              <div className="cp-card-h"><h3>What this proves</h3></div>
              <p className="small" style={{ marginTop: 0 }}>{r.statement}</p>
              <dl className="rcpt-dl">
                <dt>Data subject</dt>
                <dd>{subject.name || '—'}</dd>
                <dt>Consent receipt</dt>
                <dd>{proves.receipt_id ? receiptLink(proves.receipt_id) : '—'}</dd>
                <dt>Chat account</dt>
                <dd>
                  {NETWORK_NAMES[identity.network] || identity.network || '—'}
                  {identity.chat_address && <div className="monospace small">{identity.chat_address}</div>}
                </dd>
                <dt>Code received</dt>
                <dd className="monospace small">{evidence.received_at || '—'}</dd>
                <dt>Message</dt>
                <dd className="monospace small" style={{ wordBreak: 'break-all' }}>{evidence.message_event_id || '—'}</dd>
                <dt>Code hash</dt>
                <dd className="monospace small" style={{ wordBreak: 'break-all' }}>
                  {evidence.code_sha256 || '—'}
                  {evidence.code_hash_scheme && <div className="cp-src">{evidence.code_hash_scheme}</div>}
                </dd>
                <dt>Signed at</dt>
                <dd className="monospace small">{r.signed_at || '—'}</dd>
              </dl>
            </div>
            ) : (
            <div className="cp-card">
              <div className="cp-card-h"><h3>What was agreed</h3></div>
              <dl className="rcpt-dl">
                <dt>Data subject</dt>
                <dd>
                  {subject.name || '—'}
                  {/* The person, by user ID — not whichever handset they used. */}
                  {subject.uid && <div className="monospace small">{subject.uid}</div>}
                </dd>

                <dt>Decision</dt>
                <dd>{r.action || '—'}</dd>

                <dt>Purposes</dt>
                <dd>
                  {Array.isArray(r.purposes) && r.purposes.length > 0
                    ? <ul className="rcpt-purposes">{r.purposes.map((p: string) => <li key={p}>{p}</li>)}</ul>
                    : <span className="small">none — consent withdrawn</span>}
                </dd>

                <dt>Applies to</dt>
                <dd className="small">
                  {applies.future_conversations ? 'Future conversations' : 'Future conversations not included'}
                  {typeof applies.existing_conversations_updated === 'number' && (
                    <> · {applies.existing_conversations_updated} existing
                    conversation{applies.existing_conversations_updated === 1 ? '' : 's'} updated</>
                  )}
                </dd>

                <dt>Signed at</dt>
                <dd className="monospace small">{r.signed_at || '—'}</dd>

                {r.asserted_by && (
                  <>
                    <dt>Asserted by</dt>
                    <dd className="small">
                      {r.asserted_by === 'data_subject' ? 'the data subject' : r.asserted_by}
                      {r.asserted_via && <span className="cp-src"> (via {r.asserted_via})</span>}
                    </dd>
                  </>
                )}

                <dt>Agreement</dt>
                <dd className="small">
                  version {agreement.version || '—'}
                  {agreement.sha256 && (
                    <div className="monospace cp-src" style={{ wordBreak: 'break-all' }}>
                      sha256 {agreement.sha256}
                    </div>
                  )}
                </dd>

                {verified.proven_by && verified.proven_by.length > 0 && (
                  <>
                    <dt>Proven by</dt>
                    <dd>{verified.proven_by.map(id => <div key={id}>{receiptLink(id)}</div>)}</dd>
                  </>
                )}
              </dl>
            </div>
            )}

            <div className="cp-card">
              <div className="cp-card-h">
                <h3>Verify this yourself</h3>
                <span className="cp-src">{verified.algorithm}</span>
              </div>
              <p className="small" style={{ marginTop: 0 }}>
                The signed bytes, signature and public key are all you need — checking
                a receipt does not require this platform&rsquo;s cooperation, and nobody
                should have to take its word for it.
              </p>
              <dl className="rcpt-dl">
                <dt>Signature</dt>
                <dd className="monospace small" style={{ wordBreak: 'break-all' }}>{verified.signature_b64}</dd>
                <dt>Public key</dt>
                <dd className="monospace small" style={{ wordBreak: 'break-all' }}>{verified.public_key_b64}</dd>
              </dl>
              {/* Collapsed by default: the canonical bytes are the least readable
                  and least often wanted part, but they are what the signature is
                  over, so they must be reachable. */}
              <button type="button" className="rcpt-toggle" onClick={() => setShowBytes(v => !v)}>
                {showBytes ? 'Hide signed bytes' : 'Show signed bytes'}
              </button>
              {showBytes && (
                <pre className="modal-xml" style={{ maxHeight: '220px', marginTop: '8px' }}>
                  {verified.receipt_json}
                </pre>
              )}
            </div>

            <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="button" className="cp-gold-btn" onClick={() => downloadReceiptBundle(verified)}>
                Download signed receipt
              </button>
              <a className="rcpt-toggle" href={`/api/portal_receipt_verify.php?receipt_id=${encodeURIComponent(receiptId)}`}
                 target="_blank" rel="noreferrer">
                Open as a page
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Display + inline editor for a proof-of-acquisition pair. Renders the
// stored evidence as a chip (with the reference on hover), or the editor
// when this row is being edited.
const ProofCell: React.FC<{
  proofType: string | null;
  proofReference: string | null;
  editing: boolean;
  busy: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (proofType: string, proofReference: string) => void;
  // When the proof is a portal receipt, clicking the chip opens it
  onOpenReceipt?: (receiptId: string) => void;
}> = ({ proofType, proofReference, editing, busy, onEdit, onCancel, onSave, onOpenReceipt }) => {
  const [draftType, setDraftType] = useState(proofType || '');
  const [draftRef, setDraftRef] = useState(proofReference || '');

  useEffect(() => {
    if (editing) {
      setDraftType(proofType || '');
      setDraftRef(proofReference || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  if (!editing) {
    const clickable = proofType === 'portal_receipt' && proofReference && onOpenReceipt;
    return (
      <>
        {proofType ? (
          <span
            className="cp-chip"
            title={clickable ? `View signed receipt ${proofReference}` : (proofReference || 'no reference recorded')}
            style={clickable ? { cursor: 'pointer', textDecoration: 'underline' } : undefined}
            onClick={clickable ? () => onOpenReceipt!(proofReference!) : undefined}
          >
            {PROOF_LABELS[proofType] || proofType}
          </span>
        ) : (
          <span className="small" title="No evidence attached — bare assertion">—</span>
        )}{' '}
        <button type="button" className="cp-x cp-x-dark" disabled={busy} title="Edit proof of acquisition"
          onClick={onEdit}>✎</button>
      </>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={draftType} onChange={e => setDraftType(e.target.value)}>
        <option value="">none</option>
        {CONSENT_PROOF_TYPES.map(t => <option key={t} value={t}>{PROOF_LABELS[t] || t}</option>)}
      </select>
      <input type="text" placeholder="reference (doc id, receipt, ...)" size={16} value={draftRef}
        onChange={e => setDraftRef(e.target.value)} />
      <button type="button" disabled={busy} onClick={() => onSave(draftType, draftRef)}>Save</button>
      <button type="button" disabled={busy} onClick={onCancel}>Cancel</button>
    </span>
  );
};

// ---------------------------------------------------------------------------
// Party registry sub-page

const PartyRegistrySection: React.FC = () => {
  const [parties, setParties] = useState<PartyConsentEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [scope, setScope] = useState<ScopeSelection>({});
  const [busy, setBusy] = useState(false);
  const [newPartyTel, setNewPartyTel] = useState('');
  const [newPartyName, setNewPartyName] = useState('');
  // 'defaults' = copy the domain's default purposes (entry stays "auto");
  // 'explicit' = pick purposes here (entry becomes "admin"-managed).
  const [newPartyMode, setNewPartyMode] = useState<'defaults' | 'explicit'>('defaults');
  const [newPartyFlags, setNewPartyFlags] = useState<ConsentFlags>({ consent_recording: true });
  const [editingProofId, setEditingProofId] = useState<number | null>(null);
  const [viewReceiptId, setViewReceiptId] = useState<string | null>(null);

  const fetchParties = async (search: string = appliedSearch) => {
    setError(null);
    try {
      setParties(await consentAdminService.listParties(search || undefined, scope));
    } catch (err) {
      console.error('Error fetching party registry:', err);
      setError('Failed to fetch the party registry');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParties(appliedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch, scope.territory, scope.domain]);

  const toggleFlag = async (party: PartyConsentEntry, key: FlagKey) => {
    setBusy(true);
    setError(null);
    try {
      await consentAdminService.updateParty(party.id, { [key]: !party[key] });
      await fetchParties();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update party consent'));
    } finally {
      setBusy(false);
    }
  };

  // Basis and proof are saved separately because they answer separate
  // questions: changing what evidence exists should not restate why the
  // processing is lawful, and vice versa.
  const saveBasis = async (party: PartyConsentEntry, basis: string) => {
    setBusy(true);
    setError(null);
    try {
      await consentAdminService.updateParty(party.id, { lawful_basis: basis } as ConsentProof);
      await fetchParties();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update lawful basis'));
    } finally {
      setBusy(false);
    }
  };

  const saveProof = async (party: PartyConsentEntry, proofType: string, proofReference: string) => {
    setBusy(true);
    setError(null);
    try {
      await consentAdminService.updateParty(party.id, {
        proof_type: proofType,
        proof_reference: proofReference,
      } as ConsentProof);
      setEditingProofId(null);
      await fetchParties();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update proof'));
    } finally {
      setBusy(false);
    }
  };

  const deleteParty = async (party: PartyConsentEntry) => {
    if (!window.confirm(`Remove ${party.party_name || party.party_tel} from the registry? It will be re-created with domain defaults the next time this party appears in a new vCon.`)) {
      return;
    }
    setBusy(true);
    try {
      await consentAdminService.deleteParty(party.id);
      await fetchParties();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to delete entry'));
    } finally {
      setBusy(false);
    }
  };

  const addParty = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await consentAdminService.addParty(
        newPartyTel.trim(),
        newPartyName.trim(),
        newPartyMode === 'explicit' ? newPartyFlags : undefined
      );
      setNewPartyTel('');
      setNewPartyName('');
      setNewPartyMode('defaults');
      setNewPartyFlags({ consent_recording: true });
      await fetchParties();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to add party'));
    } finally {
      setBusy(false);
    }
  };

  const requestConsent = async (party: PartyConsentEntry) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const invite = await portalAdminService.createInvite(
        party.party_tel, party.party_name || '',
        ['recording', 'transcription', 'analysis', 'ai_training'],
        7, party.party_uid
      );
      const url = `${window.location.origin}${invite.portal_path}`;
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard denied — link still shown */ }
      setNotice(`Portal link for ${party.party_name || party.party_tel} (copied to clipboard, valid 7 days): ${url}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to create consent request'));
    } finally {
      setBusy(false);
    }
  };

  const backfill = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await consentAdminService.backfill();
      setNotice(`Sync complete: ${result.new_parties_registered} new part${result.new_parties_registered === 1 ? 'y' : 'ies'} registered (${result.total_parties} total).`);
      await fetchParties();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to sync from vCons'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="loading">Loading party registry...</div>;

  return (
    <>
      {error && <div className="error" style={{ marginTop: '12px' }}>{error}</div>}
      {notice && <div className="cp-notice">{notice}</div>}

      <div className="cp-card">
        <div className="cp-card-h">
          <h3>Party consent registry</h3>
          <span className="cp-src">stamped as consent attachments · ledgered as vcon_consent_accepted</span>
        </div>

        <ScopeFilter value={scope} onChange={setScope} />

        <form className="search-form" onSubmit={e => { e.preventDefault(); setAppliedSearch(searchText.trim()); }}>
          <input type="text" placeholder="Search by name, number, or domain..." value={searchText}
            onChange={e => setSearchText(e.target.value)} />
          <button type="submit">Search</button>
          <button type="button" onClick={() => { setSearchText(''); setAppliedSearch(''); }}>Clear</button>
          <button type="button" className="cp-gold-btn" disabled={busy} onClick={backfill}
            title="Register every party appearing in the existing vCon archive (never overwrites configured entries)">
            {busy ? 'Working…' : 'Sync from vCons'}
          </button>
        </form>

        <div className="table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Party</th>
                <th>User ID</th>
                <th>Domain</th>
                {PURPOSES.map(purpose => (
                  <th key={purpose.key}><span className="cp-ev">{purpose.label}</span></th>
                ))}
                <th title="WHY the processing is lawful (GDPR Article 6)">Basis</th>
                <th title="HOW that basis was established, and what can be shown for it">Proof</th>
                <th>Source</th>
                <th>Seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {parties.length === 0 ? (
                <tr><td colSpan={10} className="no-data">
                  No parties registered — press "Sync from vCons" to populate from the archive.
                </td></tr>
              ) : (
                parties.map(party => (
                  <tr key={party.id}>
                    <td>{party.party_name || '—'}</td>
                    {/* The uid alone. Showing the address alongside it implies
                        the consent is tied to that device, when a user may have
                        called from several — party_tel holds only the most
                        recent, so displaying it would be actively misleading.
                        Off-net parties have no uid and show their number,
                        which is the only identifier they have. */}
                    <td className="monospace small">
                      {party.party_uid
                        || <span title="Off-net party — no NetSapiens account">{party.party_tel}</span>}
                    </td>
                    <td className="monospace small">{party.domain || '—'}</td>
                    {PURPOSES.map(purpose => (
                      <td key={purpose.key} className="cp-flag-cell">
                        <input type="checkbox" disabled={busy} checked={party[purpose.key]}
                          title={`${purpose.label} consent for future vCons`}
                          onChange={() => toggleFlag(party, purpose.key)} />
                      </td>
                    ))}
                    <td className="small">
                      <select
                        value={party.lawful_basis || 'consent'}
                        disabled={busy}
                        title={LAWFUL_BASIS_LABELS[party.lawful_basis || 'consent']}
                        onChange={e => saveBasis(party, e.target.value)}>
                        {CONSENT_LAWFUL_BASES.map(b => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </td>
                    <td className="small">
                      <ProofCell
                        proofType={party.proof_type}
                        proofReference={party.proof_reference}
                        editing={editingProofId === party.id}
                        busy={busy}
                        onEdit={() => setEditingProofId(party.id)}
                        onCancel={() => setEditingProofId(null)}
                        onSave={(t, r) => saveProof(party, t, r)}
                        onOpenReceipt={setViewReceiptId}
                      />
                    </td>
                    <td>
                      <span
                        className={`cp-chip ${party.source === 'console' ? 'cp-chip-indigo' : ''} ${party.source === 'party' ? 'cp-chip-party' : ''}`}
                        title={party.source === 'party'
                          ? 'party: the data subject asserted this themselves through the consent portal (signed receipt) — the strongest tier; edit with care'
                          : party.source === 'console'
                            ? 'admin: an administrator set these purposes explicitly; domain-default changes won\'t touch them'
                            : 'auto: purposes came from the domain defaults when this party was first seen — no administrator has curated this entry'}
                      >
                        {party.source === 'console' ? 'admin' : party.source === 'party' ? 'party ✓' : 'auto'}
                      </span>
                    </td>
                    <td className="small">
                      {party.vcon_count} vCon{party.vcon_count === 1 ? '' : 's'}
                      <div>{party.last_seen_at ? formatDateTime(party.last_seen_at) : 'never'}</div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" disabled={busy} title="Create a portal link for this party to sign their own consent"
                        onClick={() => requestConsent(party)}>Request consent</button>{' '}
                      <button type="button" disabled={busy} onClick={() => deleteParty(party)}>Remove</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <form className="cp-addrow cp-addrow-light" onSubmit={addParty}>
          <input type="text" placeholder="Party address (tel/AOR, e.g. sip:2001@pbx.example.com)" required
            value={newPartyTel} onChange={e => setNewPartyTel(e.target.value)} />
          <input type="text" placeholder="Name (optional)" value={newPartyName}
            onChange={e => setNewPartyName(e.target.value)} />
          <label className="cp-radio">
            <input type="radio" name="new-party-mode" checked={newPartyMode === 'defaults'}
              onChange={() => setNewPartyMode('defaults')} />
            Copy from domain defaults
          </label>
          <label className="cp-radio">
            <input type="radio" name="new-party-mode" checked={newPartyMode === 'explicit'}
              onChange={() => setNewPartyMode('explicit')} />
            Set explicitly:
          </label>
          {newPartyMode === 'explicit' && PURPOSES.map(purpose => (
            <label key={purpose.key} className="cp-check cp-check-light">
              <input type="checkbox" checked={!!newPartyFlags[purpose.key]}
                onChange={e => setNewPartyFlags(prev => ({ ...prev, [purpose.key]: e.target.checked }))} />
              {purpose.label}
            </label>
          ))}
          <button type="submit" className="cp-gold-btn" disabled={busy || !newPartyTel.trim()}>
            Add party
          </button>
          <span className="cp-hint">
            {newPartyMode === 'defaults'
              ? 'The entry takes its domain\'s current defaults and stays "auto".'
              : 'Explicit choices mark the entry "admin"-managed.'}
          </span>
        </form>
      </div>

      {viewReceiptId && <ReceiptModal receiptId={viewReceiptId} onClose={() => setViewReceiptId(null)} />}
    </>
  );
};

// ---------------------------------------------------------------------------
// Domain defaults sub-page — table layout, built to scale to hundreds of
// domains (server-side search; the '*' fallback stays pinned on top).

const DomainDefaultsSection: React.FC = () => {
  const [domains, setDomains] = useState<ConsentDomainDefault[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newDomainFlags, setNewDomainFlags] = useState<ConsentFlags>({ consent_recording: true });
  const [newDomainProofType, setNewDomainProofType] = useState('');
  const [newDomainProofRef, setNewDomainProofRef] = useState('');
  const [editingProofId, setEditingProofId] = useState<number | null>(null);

  const fetchDomains = async (search: string = appliedSearch) => {
    setError(null);
    try {
      setDomains(await consentAdminService.listDomains(search || undefined));
    } catch (err) {
      console.error('Error fetching domain defaults:', err);
      setError('Failed to fetch domain defaults');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains(appliedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch]);

  const toggleFlag = async (domain: ConsentDomainDefault, key: FlagKey) => {
    setBusy(true);
    setError(null);
    try {
      await consentAdminService.saveDomain(domain.domain, { ...flagsOf(domain), [key]: !domain[key] });
      await fetchDomains();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update domain defaults'));
    } finally {
      setBusy(false);
    }
  };

  // saveDomain() carries the stored proof forward when no proof keys are
  // sent, so changing the basis alone leaves the evidence untouched.
  const saveDomainBasis = async (domain: ConsentDomainDefault, basis: string) => {
    setBusy(true);
    setError(null);
    try {
      await consentAdminService.saveDomain(domain.domain, flagsOf(domain), { lawful_basis: basis });
      await fetchDomains();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update lawful basis'));
    } finally {
      setBusy(false);
    }
  };

  const saveDomainProof = async (domain: ConsentDomainDefault, proofType: string, proofReference: string) => {
    setBusy(true);
    setError(null);
    try {
      await consentAdminService.saveDomain(domain.domain, flagsOf(domain),
        { proof_type: proofType, proof_reference: proofReference });
      setEditingProofId(null);
      await fetchDomains();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update proof'));
    } finally {
      setBusy(false);
    }
  };

  const deleteDomain = async (domain: ConsentDomainDefault) => {
    if (!window.confirm(`Remove the defaults for ${domain.domain}? New parties from that domain will fall back to *.`)) {
      return;
    }
    setBusy(true);
    try {
      await consentAdminService.deleteDomain(domain.domain);
      await fetchDomains();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to delete domain'));
    } finally {
      setBusy(false);
    }
  };

  const addDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await consentAdminService.saveDomain(newDomain.trim().toLowerCase(), newDomainFlags,
        newDomainProofType ? { proof_type: newDomainProofType, proof_reference: newDomainProofRef } : undefined);
      setNewDomain('');
      setNewDomainFlags({ consent_recording: true });
      setNewDomainProofType('');
      setNewDomainProofRef('');
      await fetchDomains();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to add domain'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="loading">Loading domain defaults...</div>;

  return (
    <>
      {error && <div className="error" style={{ marginTop: '12px' }}>{error}</div>}

      <div className="cp-plane">
        <div className="cp-plane-top">
          <div className="cp-pt">Domain defaults <span className="cp-pt-sm">· what a new party from each domain consents to</span></div>
          <div className="cp-pbadge">POLICY · APPLIED AT CREATION</div>
        </div>
        <form className="cp-addrow" onSubmit={addDomain}>
          <input type="text" placeholder="domain (e.g. pbx.example.com)" value={newDomain}
            onChange={e => setNewDomain(e.target.value)} />
          {PURPOSES.map(purpose => (
            <label key={purpose.key} className="cp-check">
              <input type="checkbox" checked={!!newDomainFlags[purpose.key]}
                onChange={e => setNewDomainFlags(prev => ({ ...prev, [purpose.key]: e.target.checked }))} />
              {purpose.label}
            </label>
          ))}
          <select value={newDomainProofType} onChange={e => setNewDomainProofType(e.target.value)}
            title="Standing evidence for this domain's consent (inherited by auto-enrolled parties)">
            <option value="">proof: none</option>
            {CONSENT_PROOF_TYPES.map(t => <option key={t} value={t}>proof: {PROOF_LABELS[t] || t}</option>)}
          </select>
          {newDomainProofType && (
            <input type="text" placeholder="evidence reference (e.g. MSA-2026-001)" value={newDomainProofRef}
              onChange={e => setNewDomainProofRef(e.target.value)} />
          )}
          <button type="submit" className="cp-gold-btn" disabled={busy || !newDomain.trim()}>Add domain</button>
        </form>
        <div className="cp-pnote">Unconfigured domains resolve to the * fallback. Changes affect future vCons only.</div>
      </div>

      <div className="cp-card">
        <div className="cp-card-h">
          <h3>Configured domains</h3>
          <span className="cp-src">{domains.length} shown · lookup order: exact domain → *</span>
        </div>

        <form className="search-form" onSubmit={e => { e.preventDefault(); setAppliedSearch(searchText.trim()); }}>
          <input type="text" placeholder="Search domains..." value={searchText}
            onChange={e => setSearchText(e.target.value)} />
          <button type="submit">Search</button>
          <button type="button" onClick={() => { setSearchText(''); setAppliedSearch(''); }}>Clear</button>
        </form>

        <div className="table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Domain</th>
                {PURPOSES.map(purpose => (
                  <th key={purpose.key}><span className="cp-ev">{purpose.label}</span></th>
                ))}
                <th title="WHY processing at this domain is lawful; inherited by parties auto-enrolled from it">Basis</th>
                <th>Proof</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {domains.map(domain => (
                <tr key={domain.id} className={domain.domain === '*' ? 'cp-fallback-row' : ''}>
                  <td className="monospace">
                    {domain.domain === '*' ? (
                      <>* <span className="cp-fbadge">FALLBACK</span></>
                    ) : domain.domain}
                  </td>
                  {PURPOSES.map(purpose => (
                    <td key={purpose.key} className="cp-flag-cell">
                      <input type="checkbox" disabled={busy} checked={!!domain[purpose.key]}
                        onChange={() => toggleFlag(domain, purpose.key)} />
                    </td>
                  ))}
                  <td className="small">
                    <select
                      value={domain.lawful_basis || 'consent'}
                      disabled={busy}
                      title={LAWFUL_BASIS_LABELS[domain.lawful_basis || 'consent']}
                      onChange={e => saveDomainBasis(domain, e.target.value)}>
                      {CONSENT_LAWFUL_BASES.map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </td>
                  <td className="small">
                    <ProofCell
                      proofType={domain.proof_type}
                      proofReference={domain.proof_reference}
                      editing={editingProofId === domain.id}
                      busy={busy}
                      onEdit={() => setEditingProofId(domain.id)}
                      onCancel={() => setEditingProofId(null)}
                      onSave={(t, r) => saveDomainProof(domain, t, r)}
                    />
                  </td>
                  <td className="small">{formatDateTime(domain.updated_at)}</td>
                  <td>
                    {domain.domain !== '*' && (
                      <button type="button" disabled={busy} onClick={() => deleteDomain(domain)}>Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Consent requests sub-page: portal invites — pending links, responses,
// receipts, cancellation.

const ConsentRequestsSection: React.FC = () => {
  const [invites, setInvites] = useState<ConsentInvite[]>([]);
  const [receipts, setReceipts] = useState<ConsentReceiptSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [receiptSearch, setReceiptSearch] = useState('');
  const [viewReceiptId, setViewReceiptId] = useState<string | null>(null);

  const fetchInvites = async () => {
    setError(null);
    try {
      const [inv, rec] = await Promise.all([
        portalAdminService.listInvites(),
        portalAdminService.listReceipts(),
      ]);
      setInvites(inv);
      setReceipts(rec);
    } catch (err) {
      console.error('Error fetching invites:', err);
      setError('Failed to fetch consent requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const searchReceipts = async (e: React.FormEvent) => {
    e.preventDefault();
    setReceipts(await portalAdminService.listReceipts(receiptSearch.trim() || undefined));
  };

  const copyLink = async (invite: ConsentInvite) => {
    const url = `${window.location.origin}${invite.portal_path}`;
    try {
      await navigator.clipboard.writeText(url);
      setNotice(`Link copied: ${url}`);
    } catch {
      window.prompt('Copy the portal link:', url);
    }
  };

  const cancel = async (invite: ConsentInvite) => {
    if (!window.confirm(`Cancel the pending consent request for ${invite.party_name || invite.party_tel}? The link stops working immediately.`)) {
      return;
    }
    setBusy(true);
    try {
      await portalAdminService.cancelInvite(invite.id);
      await fetchInvites();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to cancel'));
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = (status: ConsentInvite['status']) => {
    const cls = status === 'responded' ? 'status-completed'
      : status === 'pending' ? 'status-active'
      : 'status-terminated';
    return <span className={`status ${cls}`}>{status}</span>;
  };

  if (loading) return <div className="loading">Loading consent requests...</div>;

  return (
    <>
      {error && <div className="error" style={{ marginTop: '12px' }}>{error}</div>}
      {notice && <div className="cp-notice" style={{ wordBreak: 'break-all' }}>{notice}</div>}

      <div className="cp-card">
        <div className="cp-card-h">
          <h3>Consent requests</h3>
          <span className="cp-src">portal invites · signed receipts on response</span>
        </div>
        <p className="settings-hint">
          Create requests from the Party Registry ("Request consent"), or over MCP with the
          request_consent tool. Deliver each link to its party out-of-band; responses land here
          and in the registry as party-asserted consent.
        </p>

        <div className="table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Party</th>
                <th>Offered</th>
                <th>Status</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Response</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.length === 0 ? (
                <tr><td colSpan={7} className="no-data">No consent requests yet</td></tr>
              ) : (
                invites.map(invite => (
                  <tr key={invite.id}>
                    <td>
                      {invite.party_name || '—'}
                      <div className="monospace small">{invite.party_uid || invite.party_tel}</div>
                    </td>
                    <td className="small">
                      {invite.offered_purposes.map(p => <span key={p} className="cp-ev" style={{ marginRight: '3px' }}>{p}</span>)}
                    </td>
                    <td>{statusBadge(invite.status)}</td>
                    <td className="small">
                      {formatDateTime(invite.created_at)}
                      <div>{invite.created_by || ''}</div>
                    </td>
                    <td className="small">{formatDateTime(invite.expires_at)}</td>
                    <td className="small">
                      {invite.used_at ? (
                        <>
                          {formatDateTime(invite.used_at)}
                          {invite.last_receipt_id && (
                            <div>
                              <button type="button" className="monospace small"
                                title="View / download the signed receipt"
                                onClick={() => setViewReceiptId(invite.last_receipt_id!)}>
                                {invite.last_receipt_id}
                              </button>
                            </div>
                          )}
                        </>
                      ) : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {(invite.status === 'pending' || invite.status === 'responded') && (
                        <button type="button" onClick={() => copyLink(invite)}>Copy link</button>
                      )}{' '}
                      {invite.status === 'pending' && (
                        <button type="button" className="danger" disabled={busy} onClick={() => cancel(invite)}>Cancel</button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="cp-card">
        <div className="cp-card-h">
          <h3>Signed receipts</h3>
          <span className="cp-src">the proof-of-consent record · Ed25519 · publicly verifiable</span>
        </div>
        <form className="search-form" onSubmit={searchReceipts}>
          <input type="text" placeholder="Search by party name, number, or receipt id..." value={receiptSearch}
            onChange={e => setReceiptSearch(e.target.value)} />
          <button type="submit">Search</button>
        </form>
        <div className="table-container">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Receipt</th>
                <th>Party</th>
                <th>Action</th>
                <th>Purposes</th>
                <th>Applied to</th>
                <th>Signed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {receipts.length === 0 ? (
                <tr><td colSpan={7} className="no-data">No receipts yet — receipts appear when parties respond via the portal</td></tr>
              ) : (
                receipts.map(receipt => (
                  <tr key={receipt.receipt_id}>
                    <td className="monospace small">{receipt.receipt_id}</td>
                    <td className="small">
                      {receipt.party_name || '—'}
                      <div className="monospace">{receipt.party_uid || receipt.party_tel}</div>
                    </td>
                    <td>
                      <span className={`status ${receipt.action === 'revoke' ? 'status-failed' : 'status-completed'}`}>
                        {receipt.action === 'identity_proof' ? 'proof' : receipt.action}
                      </span>
                      {receipt.proves_receipt_id && (
                        <div className="monospace small">of {receipt.proves_receipt_id}</div>
                      )}
                    </td>
                    <td className="small">
                      {receipt.purposes.length > 0
                        ? receipt.purposes.map(p => <span key={p} className="cp-ev" style={{ marginRight: '3px' }}>{p}</span>)
                        : '—'}
                    </td>
                    <td className="small">
                      {receipt.action === 'identity_proof'
                        ? '—'
                        : <>future{receipt.applied_to_existing > 0 ? ` + ${receipt.applied_to_existing} existing` : ''}</>}
                    </td>
                    <td className="small">{formatDateTime(receipt.signed_at)}</td>
                    <td>
                      <button type="button" onClick={() => setViewReceiptId(receipt.receipt_id)}>
                        View / Download
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewReceiptId && <ReceiptModal receiptId={viewReceiptId} onClose={() => setViewReceiptId(null)} />}
    </>
  );
};

// ---------------------------------------------------------------------------

const ConsentPage: React.FC = () => {
  const [tab, setTab] = useState<'parties' | 'domains' | 'requests'>('parties');

  return (
    <div className="consent-page">
      <div className="cp-eyebrow">Consent authority · Party registry &amp; domain policy</div>
      <h1 className="cp-h1">Consent management</h1>
      <div className="cp-sub">
        Defaults applied when a <b>new</b> vCon is created: a registered party contributes its
        configured purposes; an unknown party is auto-registered from its domain's defaults.
        Existing vCons keep their own consent — manage those on the VCons page.
      </div>

      <nav className="settings-subnav" style={{ marginTop: '14px' }}>
        <button className={`nav-button ${tab === 'parties' ? 'active' : ''}`} onClick={() => setTab('parties')}>
          Party Registry
        </button>
        <button className={`nav-button ${tab === 'domains' ? 'active' : ''}`} onClick={() => setTab('domains')}>
          Domain Defaults
        </button>
        <button className={`nav-button ${tab === 'requests' ? 'active' : ''}`} onClick={() => setTab('requests')}>
          Consent Requests
        </button>
      </nav>

      {tab === 'parties' && <PartyRegistrySection />}
      {tab === 'domains' && <DomainDefaultsSection />}
      {tab === 'requests' && <ConsentRequestsSection />}
    </div>
  );
};

export default ConsentPage;
